<template>
  <div
    class="chart relative h-100 flex column"
    :class="{ 'dark-mode': darkMode }">
    <h3 class="sb2-chart-title">
      <span class="ellipsis">{{ title }}</span>
      <router-link
        v-if="!hideReportBtn && !isIntegration"
        :to="{ name: 'Report', params: { section: reportTitle.toLowerCase() } }"
        class="report-btn"
        tag="span">
        VIEW REPORT
      </router-link>
      <router-link
        v-if="!hideReportBtn && isIntegration"
        :to="{ name: 'ReportIntegration', params: { integration: integrationNameCondensed } }"
        class="report-btn"
        tag="span">
        VIEW REPORT
      </router-link>
    </h3>
    <template v-if="waitingForData">
      <!-- LOADING data -->
      <div class="loading-chart greedy flex column center-all">
        <span class="animated-chart-wrapper flex center-all">
          <ChartLoader
            :dark-mode="darkMode"
            width="28" />
        </span>
        <p>Loading data</p>
      </div>
    </template>
    <!-- NO DATA view -->
    <template v-else-if="noData">
      <div class="loading-chart greedy flex column center-all">
        <span class="animated-chart-wrapper flex center-all">
          <span class="emoji opacity-85">😐</span>
        </span>
        <p>No data</p>
      </div>
    </template>
    <!-- ERROR view -->
    <template v-else-if="showError">
      <div class="loading-chart greedy flex column center-all">
        <span class="animated-chart-wrapper flex center-all">
          <SBIcon
            :icon="SBIconList.ERROR"
            :color="SBColors.greyPrimary"
            :size="18"/>
        </span>
        <p>{{ error }}</p>
      </div>
    </template>
    <!-- SINGLE value view -->
    <template v-else-if="singleValueChart">
      <div class="single-value-chart greedy flex column center-all">
        <span
          ref="singleValue"
          class="single-value flex center-all" >
          <!-- {{ numberWithCommas(chartSeries[0].data[0]) }} -->
          <ICountUp
            :delay="100"
            :end-val="chartSeries[0].data[0]"
            :options="countupOptions"
            @ready="windowSizeChange"
          />
          <span class="suffix"> {{ valueSuffix }}</span>
        </span>
        <div
          v-if="type === 'line' && !hideDetails"
          class="details flex flex-align-center" >
          <template v-for="(element, index) in detailsElements" >
            <DotSeparator
              v-if="index"
              :key="`dot-${index}`"
              :size="3"
              class="ml-2 mr-2 no-shrink" />
            <span
              :key="`value-${index}`"
              class="value" >{{ Number(dataSeries[0].data[0][element.name]) || dataSeries[0].data[0][element.name] === 0
                ? Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(dataSeries[0].data[0][element.name])
                : 0
              }}
            </span>
            <p
              :key="`text-${index}`"
              :style="{
                'flex-shrink': element.text.length < 6 ? 0 : element.text.length < 10 ? 1 : 2,
              }">{{ element.text }}</p>
          </template>
        </div>
      </div>
    </template>
    <!-- Chart view -->
    <template v-else>
      <div class="info flex flex-align-center">
        <p
          v-if="type === 'line'"
          class="value flex"
          :class="{muted: (total && total.sum === '😐 No activity') || (hoverIndexData && hoverIndexData.sum === '😐 No activity')}" >
          {{ hoverIndexData === null ? (!total || (total.sum === '😐 No activity') ? '' : total.sum) : (hoverIndexData.sum === '😐 No activity' ? '' : hoverIndexData.sum) }}
          <template v-if="(hoverIndexData && hoverIndexData.sum === '😐 No activity') || (!hoverIndexData && (!total || total.sum === '😐 No activity'))">
            <span
              class="mr-2"
              :class="{'opacity-85': darkMode}">😐</span>
            <p>No activity</p>
          </template>

        </p>
        <p
          v-if="type === 'bar'"
          class="value" >{{ (hoverIndex !== null && hoverIndex >= 0) ? barChartOptions.xaxis.categories[hoverIndex] : maxValueIndex >= 0 ? barChartOptions.xaxis.categories[maxValueIndex] : barChartOptions.xaxis.categories[0] }}</p>
        <p
          v-if="valueSuffix && !((hoverIndexData && hoverIndexData.sum === '😐 No activity') || (!hoverIndexData && (!total || total.sum === '😐 No activity')))"
          class="suffix" >{{ valueSuffix }}</p>
      </div>
      <div
        v-if="type === 'line' && !hideDetails"
        class="details flex flex-align-center"
        :class="{ invisible: !hoverIndexData || ((hoverIndexData.sum).toString().indexOf('No activity') >= 0) }" >
        <template v-for="(element, index) in detailsElements" >
          <DotSeparator
            v-if="index && !showMaxDetail"
            :key="`dot-${index}`"
            :size="3"
            class="ml-2 mr-2 no-shrink" />
          <span
            v-if="!showMaxDetail || !hoverMax || hoverMax.name === element.name"
            :key="`value-${index}`"
            class="value" >{{
              hoverIndexData === null
                ? total[element.name]
                : Number(hoverIndexData[element.name]) || hoverIndexData[element.name] >= 0
                  ? `${hoverIndexData[element.name]}${timeValues ? '%' : ''}`
                  : '?'
            }}</span>
          <p
            v-if="!showMaxDetail || !hoverMax || hoverMax.name === element.name"
            :key="`text-${index}`"
            :style="{
              'flex-shrink': element.text.length < 6 ? 0 : element.text.length < 10 ? 1 : 2,
            }">{{ element.text }}</p>
        </template>
      </div>
      <p
        v-if="type === 'bar'"
        class="date"
        :class="{invisible: (hoverIndex === null || Number.isNaN(chartSeries[0].data[hoverIndex])) }" >{{ hoverIndex === null ? maxValue : Number(chartSeries[0].data[hoverIndex]) || Number(chartSeries[0].data[hoverIndex]) === 0 ? Number(chartSeries[0].data[hoverIndex]) : '' }}%</p>
      <VueApexCharts
        v-if="initReady"
        ref="apexChart"
        :class="[type, {'padding-bottom-bar': type === 'bar', 'padding-bottom-line': type === 'line'}]"
        :height="type === 'line' ? 190 : 216"
        :options="chartOptions"
        :series="proccessedChartSeries" />
      <div
        v-else
        style="height: 236px" />
      <div
        v-if="chartOptions.chart.type === 'line' && !hoverIndexData"
        class="line-labels flex space-between" >
        <span>{{ startLabel }}</span>
        <span>{{ endLabel }}</span>
      </div>
      <div
        v-if="chartOptions.chart.type === 'line' && hoverIndexData"
        class="line-labels flex space-between"
        :class="{invisible: !hoverIndexData}" >
        <span>{{ chartDate }}</span>
        <span class="hover-right-detail">{{ hoverRightCornerDetail }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import colorBetween from 'color-between';
import ICountUp from 'vue-countup-v2';
import { duration } from 'moment';

import DotSeparator from '@/components/common/DotSeparator';
import ChartLoader from '@/components/animatedIcons/ChartLoader';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import { isAmPm } from '@/helpers/dateHelpers';

const barChartSingleBarWidthPercentage = '85%';

export default {
  name: 'SbApexChart',
  components: {
    VueApexCharts,
    DotSeparator,
    ChartLoader,
    ICountUp,
    SBIcon,
  },
  props: {
    type: {
      type: String,
      required: false,
      default: 'line',
    },
    title: {
      type: String,
      reqired: false,
      default: null,
    },
    forceLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
    height: {
      type: [Number, String],
      required: false,
      default: null,
    },
    options: {
      type: Object,
      required: false,
      default: () => {},
    },
    dataSeries: {
      type: Array,
      required: false,
      default: null,
    },
    chartSeries: {
      type: Array,
      required: false,
      default: () => [],
    },
    detailsElements: {
      type: Array,
      required: false,
      default: () => [],
    },
    categories: {
      type: Array,
      required: false,
      default: () => [],
    },
    error: {
      type: String,
      required: false,
      default: 'Couldn\'t load chart',
    },
    noDataIfZero: {
      type: Boolean,
      required: false,
      default: true,
    },
    showError: {
      type: Boolean,
      required: false,
      default: false,
    },
    startDate: {
      type: Number,
      required: false,
      default: null,
    },
    endDate: {
      type: Number,
      required: false,
      default: null,
    },
    valueSuffix: {
      type: String,
      required: false,
      default: null,
    },
    resolution: {
      type: String,
      reqired: false,
      default: 'day',
    },
    percentage: {
      type: Boolean,
      reqired: false,
      default: false,
    },
    timeValues: {
      type: Boolean,
      reqired: false,
      default: false,
    },
    reportTitle: {
      type: String,
      required: false,
      default: 'integration',
    },
    hideReportBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    darkMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    hourly: {
      type: Boolean,
      required: false,
      default: false,
    },
    showMaxDetail: {
      type: Boolean,
      required: false,
      default: false,
    },
    colors: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    hoverRightCornerDetail: {
      type: String,
      required: false,
      default: '',
    },
    noActivityZero: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      defaultColors: {
        line: '#2680eb',
        axisBorder: '#e6ebed',
        crosshairs: '#9eafb5',
        marker: '#2680eb',
      },
      demoActive: false,
      initReady: false,
      hoverIndex: null,
      hoverMax: null,
      SBColors,
      SBIconList,
      barChartSeries: null,
      emptyBarPercentage: 3,
      countupOptions: {
        useEasing: true,
        useGrouping: true,
        separator: ',',
        decimal: '.',
        prefix: '',
        suffix: '',
        duration: 1,
      },
      barChartOptions: {
        grid: {
          show: false,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
        },
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false,
          },
          events: {
            mouseMove: (event) => {
              if (event.layerY < 40 || event.layerY > 180) {
                this.hoverIndex = null;
                return;
              }
              const width = event.srcElement.parentNode.parentNode.parentNode.offsetWidth ? event.srcElement.parentNode.parentNode.parentNode.offsetWidth : event.srcElement.parentNode.parentNode.parentNode.parentNode.parentNode.offsetWidth;
              const percentageXMove = Math.abs(event.layerX) / width;
              if (percentageXMove <= 0.2375) {
                this.hoverIndex = 0;
              } else if (percentageXMove <= 0.5) {
                this.hoverIndex = 1;
              } else if (percentageXMove <= 0.7625) {
                this.hoverIndex = 2;
              } else {
                this.hoverIndex = 3;
              }
            },
            mouseLeave: () => {
              this.hoverIndex = null;
            },
            mounted: () => {
              this.moveBarChartBarsToEdges();
            },
            updated: () => {
              this.moveBarChartBarsToEdges();
            },
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 300,
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: false,
        },
        xaxis: {
          labels: {
            show: true,
            style: {
              colors: '#9eafb5',
              fontSize: '14px',
              fontWeight: 400,
              cssClass: 'apexcharts-xaxis-label',
            },
            // hideOverlappingLabels: true, // not working
          },
          categories: this.categories,
          axisBorder: {
            show: true,
            height: 1,
            color: '#e6ebed',
          },
          axisTicks: {
            show: false,
          },
          crosshairs: {
            show: true,
            width: 1,
            position: 'front',
            opacity: 1,
            stroke: {
              color: '#9eafb5',
              width: 1,
              dashArray: 2,
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        colors: [({ value }) => {
          // When value is equal to emptyBarPercentage then this means it is actually 0 and we need to show it as a gray tiny bar.
          if (value === this.emptyBarPercentage) {
            return '#e6ebed';
          }
          const startColor = '#cfe6fd';
          const endColor = '#51a7f7';
          const percent = (value - this.emptyBarPercentage) / (100 - this.emptyBarPercentage);
          return colorBetween(startColor, endColor, percent, 'hex');
        }],
        states: {
          normal: {
            filter: {
              type: 'none',
            },
          },
          hover: {
            filter: {
              type: 'none',
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        tooltip: {
          enabled: false,
        },
        yaxis: {
          show: false,
          labels: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            columnWidth: barChartSingleBarWidthPercentage,
          },
        },
      },
    };
  },
  computed: {
    isIntegration() {
      return this.reportTitle && this.reportTitle.startsWith('integration');
    },
    integrationName() {
      return !this.isIntegration ? '' : this.reportTitle.split(':')[1];
    },
    integrationNameCondensed() {
      return this.isIntegration && this.reportTitle.split(':') && this.reportTitle.split(':')[1] ? this.reportTitle.split(':')[1].replace(/ /g, ' ') : '';
    },
    computedColors() {
      return {
        ...this.defaultColors,
        ...this.colors,
      };
    },
    singleValueChart() {
      return this.chartSeries[0].data.length === 1;
    },
    waitingForData() {
      if (this.forceLoader || this.demoActive) {
        return true;
      }
      return !this.showError && !this.chartSeries;
    },
    chartOptions() {
      return this.type === 'line'
        ? this.lineChartOptions
        : this.barChartOptions;
    },
    /**
     * Min bar size is created and all the other values are transformed to populate the rest of the space.
    */
    proccessedChartSeries() {
      let series = [];
      if (this.type === 'bar') {
        series = [...this.barChartSeries];
        this.barChartSeries[0].data.forEach((value, index) => {
          // 1. get the percentage from the max value
          const percentageFromMax = value / this.maxValue;
          // 2. how does that translates to available space which is 100 - emptyBarPercentage
          const cutInAvailableSpace = (100 - this.emptyBarPercentage) * percentageFromMax;
          // 3. real value is cutInAvailabeleSpace + emptyBarPercentage
          value = cutInAvailableSpace + this.emptyBarPercentage;
          this.barChartSeries[0].data[index] = value;
        });
      } else if (this.type === 'line') {
        series = [...this.chartSeries];
      }
      return series;
    },
    noData() {
      return !this.showError &&
        (
          !this.chartSeries ||
          !this.chartSeries[0] ||
          (this.chartSeries[0].data && !this.chartSeries[0].data.length) ||
          (this.noDataIfZero && this.maxValue === 0)
        );
    },
    errorLoading() {
      return false;
    },
    maxValue() {
      return this.chartSeries[0].data ? Math.max(...this.chartSeries[0].data) : 0;
    },
    maxValueIndex() {
      return this.chartSeries[0].data.findIndex(value => value === this.maxValue);
    },
    total() {
      const total = {
        sum: 0,
      };
      let totalPercentage = {
        contacted: 0,
        replied: 0,
      };

      if (this.dataSeries && this.dataSeries[0] && this.dataSeries[0].data) {
        let totalMessages = 0;
        this.dataSeries[0].data.forEach((data, index) => {
          if (this.percentage) {
            totalPercentage = {
              contacted: totalPercentage.contacted + data.contacted,
              replied: totalPercentage.replied + data.replied,
            };
          } else if (this.timeValues) {
            totalMessages += data.totalCount;
            total.sum += data.totalSeconds;
          } else {
            total.sum += this.chartSeries[0].data[index];
          }

          this.detailsElements.forEach((detailElement) => {
            if (!total[detailElement.name]) {
              total[detailElement.name] = 0;
            }
            if (!Number.isNaN(Number(data[detailElement.name]))) {
              if (this.timeValues) {
                total[detailElement.name] += 2;
              } else {
                total[detailElement.name] += data[detailElement.name];
              }
            } else {
              console.error('Chart value not a number');
            }
          });
        });

        if (this.percentage) {
          total.sum = totalPercentage.contacted ?
            (
              totalPercentage.replied ?
                Number(totalPercentage.replied / totalPercentage.contacted * 100).toFixed(2) :
                'ZERO'
            ) :
            0;
        } else if (this.timeValues) {
          total.sum = totalMessages ? this.durationToString(Number(total.sum / totalMessages || 0)) : '😐 No activity';
        }


        if (this.noActivityZero && total.sum === 0) {
          total.sum = '😐 No activity';
        } else if (total.sum === 'ZERO') {
          total.sum = 0;
        } else if (!this.timeValues) {
          total.sum = Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(total.sum);
        }
      }
      return total;
    },
    hoverIndexData() {
      let hoverIndexData = null;
      if (this.type === 'line' && this.hoverIndex !== null && this.hoverIndex >= 0 && this.timeValues) {
        const proccessedDetails = {};
        this.detailsElements.forEach((detailElement) => {
          proccessedDetails[detailElement.name] = Math.round(Number(this.dataSeries[0].data[this.hoverIndex][detailElement.name] * 100));
        });

        const details = {
          ...this.dataSeries[0].data[this.hoverIndex],
          ...proccessedDetails,
        };

        hoverIndexData = {
          sum: this.dataSeries[0].data[this.hoverIndex].totalCount ? this.durationToString(Number(this.dataSeries[0].data[this.hoverIndex].totalSeconds / this.dataSeries[0].data[this.hoverIndex].totalCount)) : '😐 No activity',
          ...details,
        };
      } else if (this.type === 'line' && this.hoverIndex !== null && this.hoverIndex >= 0) {
        hoverIndexData = {
          sum: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(this.chartSeries[0].data[this.hoverIndex]),
          ...this.dataSeries[0].data[this.hoverIndex],
        };
        if (this.noActivityZero && !hoverIndexData.contacted && Number(hoverIndexData.sum) === 0) {
          hoverIndexData.sum = '😐 No activity';
        }
      }

      return hoverIndexData;
    },
    chartDate() {
      const isAmPmTest = isAmPm(this.$store.getters['user/region']);

      let format = 'D MMM';
      if (this.isYearDiff) {
        format += ', YYYY';
      }
      if (this.hourly) {
        format = isAmPmTest ? 'hh:mma' : 'HH:mm';
      }
      if (this.resolution === 'month' && this.isYearDiff) {
        format = 'MMM YYYY';
      } else if (this.resolution === 'month') {
        format = 'MMM';
      }
      let returnValue = this.hoverIndex !== null && this.hoverIndex >= 0
        ? this.$moment(false, false).unix(this.dataSeries[0].data[this.hoverIndex].date).format(format)
        : this.$moment(false, false).unix(this.endDate).format(format);

      if (this.hourly && this.hoverIndex !== null && this.hoverIndex >= 0) {
        format = isAmPmTest ? 'hh:mma' : 'kk:mm';
        const endTime = this.$moment(false, false).unix(this.dataSeries[0].data[this.hoverIndex].date).add(1, 'hour').format(format);
        returnValue += ` - ${endTime.trim() === '24:00' ? '23:59' : endTime}`;
      }

      return returnValue;
    },
    dateDiff() {
      return this.$moment(false, false).unix(this.endDate).diff(this.$moment(false, false).unix(this.startDate), 'days');
    },
    isYearDiff() {
      return this.$moment(false, false).unix(this.endDate).year() !== this.$moment(false, false).unix(this.startDate).year();
    },
    startLabel() {
      // If same year and less than 60 days then - D MMM
      // else - mm yyyy
      // console.log(this.$moment(false, false).unix(this.endDate).year(), this.$moment(false, false).unix(this.startDate).year());
      // console.log('Year dif', this.isYearDiff);
      // console.log('Day dif', this.dateDiff);
      let format = 'D MMM';
      if (this.isYearDiff || this.dateDiff > 60) {
        format = 'MMM YYYY';
      }
      return this.$moment(false, false).unix(this.startDate).format(format);
    },
    endLabel() {
      let format = 'D MMM';
      if (this.isYearDiff || this.dateDiff > 60) {
        format = 'MMM YYYY';
      }
      return this.$moment(false, false).unix(this.endDate).format(format);
    },
    /** Sets how much points will be shown with dotted line meaning they are in the future. */
    pointsInFuture() {
      let counter = 0;
      if (this.dataSeries && this.dataSeries[0] && this.dataSeries[0].data && this.dataSeries[0].data.length) {
        this.dataSeries[0].data.forEach((datePoint) => {
          if (this.$moment().isBefore(this.$moment(false, false).unix(datePoint.date))) {
            counter++;
          }
        });
      }

      return counter;
    },
    lineChartOptions() {
      return {
        grid: {
          show: false,
        },
        chart: {
          type: 'line',
          toolbar: {
            show: false,
          },
          width: '100px',
          events: {
            // Needed fore line graph since we do not have markers there, just a straight line
            mouseMove: (event, chartContext, config) => {
              this.hoverIndex = config.dataPointIndex;
            },
            mouseLeave: () => {
              this.hoverIndex = null;
            },
            mounted: () => {
              this.$refs.apexChart.$el.childNodes[0].classList.add('rendered');
            },
            updated: () => {
              this.$refs.apexChart.$el.childNodes[0].classList.add('no-after');
            },
          },
          zoom: {
            enabled: false,
          },
          animations: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        forecastDataPoints: {
          count: this.pointsInFuture,
        },
        stroke: {
          show: true,
          curve: 'straight',
          lineCap: 'butt',
          colors: [this.computedColors.line],
          width: 1.5,
          dashArray: 0,
        },
        xaxis: {
          labels: {
            show: false,
          },
          axisBorder: {
            show: true,
            height: 1,
            color: this.computedColors.axisBorder,
          },
          axisTicks: {
            show: false,
          },
          crosshairs: {
            show: true,
            width: 1,
            position: 'back',
            opacity: 1,
            stroke: {
              color: this.computedColors.crosshairs,
              width: 1,
              dashArray: 2,
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        states: {
          normal: {
            filter: {
              type: 'none',
            },
          },
          hover: {
            filter: {
              type: 'none',
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        tooltip: {
          enabled: true,
          custom: () => '', // It can't be hidden without hiding the marker bar so a custom tooltip showing nothing is a optimal solution.
        },
        yaxis: {
          show: false,
          labels: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        markers: {
          size: 0,
          colors: [this.computedColors.marker],
          strokeWidth: 0,
          fillOpacity: 1,
          shape: 'circle',
          hover: {
            size: 3,
            sizeOffset: 0,
          },
        },
        legend: {
          show: false,
        },
      };
    },
  },
  watch: {
    hoverIndex() {
      this.colorTheRightBar();
      this.$emit('hover-index', this.hoverIndex);
    },
    hoverIndexData() {
      this.hoverMax = null;
      if (this.hoverIndexData) {
        const detailsValues = [];
        this.detailsElements.forEach((details) => {
          detailsValues.push(this.hoverIndexData[details.name]);
        });
        const maxValue = Math.max(...detailsValues);
        this.hoverMax = this.detailsElements.find(element => this.hoverIndexData[element.name] === maxValue);
      }
    },
    chartSeries: {
      handler() {
        if (this.type === 'bar' && this.chartSeries) {
          this.barChartSeries = JSON.parse(JSON.stringify(this.chartSeries));
        }
        this.$nextTick(() => {
          this.resizeSingleValueFont();
        });
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // There is a glitch in apex charts which renders them bigger when rendered initially. That is why we need to render it when the columns get their sizes
    setTimeout(() => {
      this.initReady = true;
    }, 500);
    setTimeout(() => {
      this.demoActive = false;
    }, 3000);
    window.addEventListener('resize', this.windowSizeChange);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.windowSizeChange);
  },
  methods: {
    isOdd(number) {
      return number % 2 === 1;
    },
    // Formats the number with commas. TODO Consider creating a directive out of this.
    // numberWithCommas(n) {
    //   return n.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
    // },
    moveBarChartBarsToEdges() {
      const barChartSingleBarWidthPercentageNumber = !Number.isNaN(parseFloat(barChartSingleBarWidthPercentage)) ? parseFloat(barChartSingleBarWidthPercentage) / 100 : 0;

      if (barChartSingleBarWidthPercentageNumber && barChartSingleBarWidthPercentageNumber !== 1) {
        const chartWidth = this.$refs.apexChart.$el.offsetWidth;
        const bars = Array.from(this.$refs.apexChart.$el.childNodes[0].childNodes[0].childNodes[0].childNodes[3].childNodes[0].childNodes).filter(element => element.classList.contains('apexcharts-bar-area'));
        const barCount = bars.length;
        const singleBarWidth = chartWidth / barCount;

        const labels = Array.from(this.$refs.apexChart.$el.childNodes[0].childNodes[0].childNodes[0].childNodes[1].childNodes[0].childNodes).filter(element => element.classList.contains('apexcharts-xaxis-label'));

        const middleBar = barCount / 2;
        const skipBar = (middleBar % 1) > 0
          ? Math.ceil(middleBar)
          : null;
        const barsOnEachSide = skipBar ? skipBar - 1 : middleBar;

        const freeSpaceToMove = (singleBarWidth * (1 - barChartSingleBarWidthPercentageNumber)) / 2;

        bars.forEach((bar, index) => {
          let movePixels = 0;
          if ((index + 1) !== skipBar && (index + 1) <= middleBar) {
            movePixels = freeSpaceToMove - (freeSpaceToMove / barsOnEachSide) * index;
            bar.style.transform = `translateX(-${movePixels}px)`;
            labels[index].style.transform = `translate(-${movePixels}px, 3px)`; // 3px on Y is because apex translates it 3px on Y by default so we need to preserve it
          } else if ((index + 1) !== skipBar && (index + 1) > middleBar) {
            movePixels = freeSpaceToMove - (freeSpaceToMove / barsOnEachSide) * (barCount - index - 1);
            bar.style.transform = `translateX(${movePixels}px)`;
            labels[index].style.transform = `translate(${movePixels}px, 3px)`;
          }

          // NOTE - Code below is used to filter out every second label so they do not overlap but it is not even close to finish because we do not have more than 4 bars at the moment and overlapping is not happening at the moment.
          //        If this will be nedded later then this can be used as an example and a start to make this feature.
          // console.log(labels.length > 6, this.isOdd(labels.length));
          // const chartRect = this.$refs.apexChart.$el.getBoundingClientRect();
          // const labelRect = labels[index].getBoundingClientRect();
          // if (index === 0 && labelRect.left < chartRect.left) {
          //   console.log('LEFT:', labelRect.left, chartRect.left);
          //   const moveRight = chartRect.left - labelRect.left;
          //   console.log('LEFT mmove:', moveRight);
          //   labels[index].style.transform = `translate(${moveRight}px, 3px)`;
          // }
          // if (labels.length > 6 && this.isOdd(labels.length) && this.isOdd(index)) {
          //   labels[index].style.transform = `translate(0px, 999px)`;
          // }
        });
      }
    },
    /** Figures out which bar is hovered and gives it an active class so it can be painted. */
    colorTheRightBar() {
      if (this.type === 'bar' && this.$refs.apexChart.$el.childNodes[0]) {
        const bars = Array.from(this.$refs.apexChart.$el.childNodes[0].childNodes[0].childNodes[0].childNodes[3].childNodes[0].childNodes).filter(element => element.classList.contains('apexcharts-bar-area'));
        bars.forEach((bar, index) => {
          if (index === this.hoverIndex) {
            bar.classList.add('active');
            if (this.chartSeries[0].data[this.hoverIndex] === 0) {
              bar.classList.add('empty');
            }
          } else {
            bar.classList.remove('active');
            bar.classList.remove('empty');
          }
        });
      }
    },
    resizeSingleValueFont() {
      if (this.$refs.singleValue) {
        const widthPercentageTaken = this.$refs.singleValue.offsetWidth / this.$refs.singleValue.offsetParent.offsetWidth;

        if (widthPercentageTaken >= 1) {
          const currentFontSize = parseInt(window.getComputedStyle(this.$refs.singleValue).fontSize, 10);
          if (currentFontSize > 20) {
            this.$refs.singleValue.style.fontSize = `${currentFontSize - 1}px`;
            this.resizeSingleValueFont();
          }
        }
      }
    },
    windowSizeChange() {
      if (this.$refs.singleValue) {
        const currentFontSize = parseInt(window.getComputedStyle(this.$refs.singleValue).fontSize, 10);
        if (currentFontSize !== 44) {
          this.$refs.singleValue.style.fontSize = '44px';
        }
        this.resizeSingleValueFont();
      }
    },
    durationToString(seconds) {
      // eslint-disable-next-line no-underscore-dangle
      const durations = duration(seconds, 'seconds')._data;

      const dayDisplay = durations.days > 0 ? `${durations.days}d ` : '';
      const hourDisplay = durations.hours > 0 ? `${durations.hours}h ` : '';
      const minuteDisplay = durations.minutes > 0 ? `${durations.minutes}m ` : '';
      const secondsDisplay = durations.seconds >= 0 && seconds < 60 ? `${durations.seconds}s` : ''; // Do not remove seconds because if the value results only in seconds then the return value will be '' and the chart will jump up on that selection. Only show seconds if result is less than 1 minute.

      return `${dayDisplay}${hourDisplay}${minuteDisplay}${secondsDisplay}`;
    },
    // drawLineOnSingleValueChart() {
    //   // <path id="SvgjsPath5758" index="0" clip-path="url(#gridRectMaskaqh9xus5)" ></path>
    //   if (this.type !== 'line') {
    //     return;
    //   }

    //   const lineChartWrapper = this.$refs.apexChart.$el.childNodes[0].childNodes[0].childNodes[0].childNodes[4].childNodes[0];
    //   const hasLine = lineChartWrapper.childNodes[0].classList.value.indexOf('apexcharts-line') >= 0;

    //   if (!hasLine) {
    //     const yPos = this.$refs.apexChart.$el.childNodes[0].childNodes[0].childNodes[0].childNodes[4].childNodes[0].childNodes[0].childNodes[0].childNodes[0].cy.baseVal.value;
    //     console.log('Y pos', yPos);
    //     const linePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    //     linePath.setAttributeNS(null, 'd', `M 0 ${yPos}L 1000 ${yPos}`);
    //     linePath.setAttributeNS(null, 'fill', 'none');
    //     linePath.setAttributeNS(null, 'fill-opacity', 1);
    //     linePath.setAttributeNS(null, 'stroke', 'rgba(38,128,235,0.85)');
    //     linePath.setAttributeNS(null, 'stroke-opacity', 1);
    //     // linePath.setAttributeNS(null, 'stroke-linecap', 'butt');
    //     linePath.setAttributeNS(null, 'stroke-width', 1.5);
    //     // linePath.setAttributeNS(null, 'stroke-dasharray', 0);
    //     linePath.classList.add('apexcharts-line');
    //     // linePath.setAttributeNS(null, 'index', 0);
    //     // linePath.setAttributeNS(null, 'clip-path', 'url(#gridRectMaskaqh9xus5)');
    //     // linePath.setAttributeNS(null, 'pathTo', 'M 0 145L 184 145L 368 13.919999999999987');
    //     // linePath.setAttributeNS(null, 'pathFrom', 'M -1 145L -1 145L 184 145L 368 145');
    //     lineChartWrapper.appendChild(linePath);
    //   }

    //   // <path id="SvgjsPath5758" d="M 0 145L 184 145L 368 13.919999999999987" fill="none" fill-opacity="1" stroke="rgba(38,128,235,0.85)" stroke-opacity="1" stroke-linecap="butt" stroke-width="1.5" stroke-dasharray="0" class="apexcharts-line" index="0" clip-path="url(#gridRectMaskaqh9xus5)" pathTo="M 0 145L 184 145L 368 13.919999999999987" pathFrom="M -1 145L -1 145L 184 145L 368 145"></path>
    //   // <path                    d="M 0 145L 184 145L 368 13.919999999999987" fill="none" fill-opacity="1" stroke="rgba(38,128,235,0.85)" stroke-opacity="1" stroke-linecap="butt" stroke-width="1.5" stroke-dasharray="0" class="apexcharts-line"           clip-path="url(#gridRectMaskaqh9xus5)" pathto="M 0 145L 184 145L 368 13.919999999999987" pathfrom="M -1 145L -1 145L 184 145L 368 145"></path>
    //   // <path                    d="M 0 145L 184 145L 368 13.919999999999987" fill="none" fill-opacity="1" stroke="rgba(38,128,235,0.85)" stroke-opacity="1" stroke-linecap="butt" stroke-width="1.5" stroke-dasharray="0" class="apexcharts-line" index="1" clip-path="url(#gridRectMaskaqh9xus5)" pathto="M 0 145L 184 145L 368 13.919999999999987" pathfrom="M -1 145L -1 145L 184 145L 368 145"></path>
    // },
  },
};
</script>

<style lang="scss" scoped>
.loading-chart {
  min-height: 279px;

  p {
    font-size: 14px;
    font-weight: 400;
    color: $greyPrimary;
  }

  .animated-chart-wrapper {
    display: block;
    width: 58px;
    height: 62px;
    border-radius: 15px;
    background-color: #f6f8f9;
    margin-bottom: 26px;

    .emoji {
      font-size: 26px;
      font-family: 'apple color emoji,android emoji,emojione mozilla,twemoji mozilla,segoe ui emoji,noto color emoji,emojisymbols,segoe ui symbol';
    }
  }
}

.muted {
  color: $blackUltralight !important;
  font-weight: 400 !important;
}

.dark-mode {
  .loading-chart {

    .animated-chart-wrapper {
      background-color: #173e51
    }

    p {
      color: #8195a0;
    }
  }

  .muted {
    color: #bfc9cf !important;
    font-weight: 400 !important;
  }
}

.single-value-chart {
  min-height: 279px;

  .single-value {
    padding: 20px 31px;
    border-radius: 15px;
    background-color: #f6f8f9;
    margin-bottom: 26px;
    font-size: 44px;
    font-weight: 500;

    span {
      font-weight: 500;
    }

    .suffix {
      font-size: 55%;
    }
  }
}

.chart {
  .sb2-chart-title {
    display: flex;

    span {
      color: $blackLight;
      font-size: 14px;
      font-weight: 500;
      text-transform: uppercase;
      margin-bottom: 9px;
      flex-grow: 2;

      &.ellipsis {
        margin-right: 12px;
      }

      &.report-btn {
        color: $bluePrimary;
        flex-grow: 0;
        flex-shrink: 0;

        &:hover {
          color: $blackPrimary;
          cursor: pointer;
        }
      }
    }
  }

  .info {
    margin-bottom: 9px;

    .value, .suffix {
      font-size: 20px;
      font-weight: 500;
      color: $blackPrimary;
    }
  }

  .details {
    overflow: hidden;
    min-height: 14px;

    .value, .suffix {
      color: $blackUltralight;
      font-size: 14px;
      font-weight: 500;
      margin-right: 5px;

      * {
        color: $blackUltralight;
        font-size: 14px;
        font-weight: 500;
      }
    }

    p {
      color: $greyPrimary;
      font-size: 14px;
      font-weight: 400;
      white-space: nowrap;
      overflow-x: clip;
      text-overflow: ellipsis;
    }
  }

  .date {
    font-size: 14px;
    font-weight: 400;
    color: $greyPrimary;
  }

  .padding-bottom-bar {
    padding-bottom: 20px;
  }

  .padding-bottom-line {
    padding-bottom: 46px;
    margin: 0 -11px 0 -12px; // This offsets the original apex chart padding. We coul use padding params on the apex options but then the first and last markers or anything that is bigger that graph is clipped.
  }

  .line-labels {
    position: absolute;
    bottom: 31px;
    padding: 0 12px;
    width: 100%;
    padding: 0;

    span {
      color: $greyPrimary;
      font-size: 14px;
      font-weight: 400;

      &.hover-right-detail {
        font-style: italic;
      }
    }
  }
}

.line ::v-deep .apexcharts-canvas {
  overflow: hidden;
  // max-width: 100%;

  .apexcharts-tooltip {
    display: none;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    background-color: white;
    width: 100%;
    height: calc(100% - 14px);
    transition: all 0.8s;
  }

  &.rendered::after {
    left: 101%;
  }

  &.no-after::after {
    transition: none;
    left: 101%;
  }
}

.chart.dark-mode {
  .sb2-chart-title span {
    color: $white;
    opacity: 0.5;
    font-size: 14px;
    font-weight: 500;
  }
  .info .value, .info .suffix {
    color: $white;
    font-size: 28px;
    font-weight: 500;
  }
  .line ::v-deep .apexcharts-canvas {
    &::after {
      background-color: #0b2a3e;
    }
  }
}

::v-deep .apexcharts-canvas {
  // max-width: 100%;
  .apexcharts-tooltip {
    display: none;
  }
}

::v-deep .apexcharts-svg {
  &.apexcharts-zoomable.hovering-zoom {
    cursor: default;
  }

  .apexcharts-line-series .apexcharts-series .apexcharts-line {
    // TODO - If path is straight line then some strange effect is shown at the start and at the end of the line. If clears if at least one dot is moved by 0.0001 on Y axis.
    // This needs to be fixed either by getting to the cause of this or dynamically substracting starting point Y value by 0.0001.
  }

  .apexcharts-bar-series {
    .apexcharts-bar-area {
      &.active {
        fill: $bluePrimary;
        &.empty {
          fill: $greyBorder;
        }
      }
    }
    .apexcharts-bar-area:hover {
      // fill: $bluePrimary;
    }

    .apexcharts-series {
      .apexcharts-bar-area {
        // transform: translateX(calc(100px - 2%));
      }
    }
  }

  .apexcharts-xaxis-label {
    transform: translateY(3px);
  }
}

</style>
